import { useQuery, useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { get } from 'lodash';
import { queryAppData, getAppDataTimeline, getAppDataTimelineEventList } from '@api/appData';
import { querySelector } from '@store/metricsChartContainer/selectors';
import instance from '@api/common';
import { getPersistentHashKey } from '@utils/persistenceUtil';
import { customerOverrideForDrilldown } from '@src/components/app-viewer-sessions/utils';
import { CLIENT_ID_FIELD, DEVICE_NAME_FIELD, USER_ID_FIELD } from '../constants';

const useGetAppDataTimeline = (viewerId: string, interval: string, clientId: string | null) => {
  const { metrics, reqBody } = useSelector(querySelector);
  // const source = axios.CancelToken.source();
  return useQuery(
    [viewerId, interval, metrics, clientId],
    async () => {
      if (!interval) {
        return {
          summaryData: {
            userInfoData: [],
            eventSummaryData: [],
          },
          sessionTableData: [],
        };
      }
      const res = await getAppDataTimeline(reqBody, {
        // cancelToken: source.token,
        viewerId,
        ts: interval,
        clientId,
      });
      return res?.data;
    },
    { enabled: !!clientId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

export const useGetAppDataTimelineEventList = (
  viewerId: string,
  interval: string,
  clientId: string | null,
  sessionId: string,
  enableNW: boolean,
) => {
  const { metrics, reqBody } = useSelector(querySelector);
  // const source = axios.CancelToken.source();
  return useQuery(
    [viewerId, interval, metrics, clientId, sessionId, enableNW],
    async () => {
      if (!interval || !sessionId) {
        return {
          eventList: [],
          requestBody: {},
        };
      }
      const res = await getAppDataTimelineEventList(reqBody, {
        // cancelToken: source.token,
        viewerId,
        ts: interval,
        clientId,
        sessionId,
        enableNW,
      });
      return res?.data;
    },
    {
      enabled: !!sessionId && !!clientId,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

function getRequestBody(options: {
  interval: string;
  mainField: string;
  selectedTrendMetric: string;
  extraGroupBy?: string[];
}) {
  const { mainField, selectedTrendMetric, interval, extraGroupBy } = options;
  let groupBy = [mainField];
  if (extraGroupBy) {
    groupBy = [mainField, ...extraGroupBy];
  }
  return [
    {
      type: 'group-by',
      dataset: 'AppExperience',
      metrics: [selectedTrendMetric],
      interval,
      granularity: 'ALL',
      filter: [
        [
          {
            display: '',
            field: mainField,
            key: null,
            op: 'not_eq',
            originalField: mainField,
            value: null,
          },
        ],
      ],
      limit: 1500,
      groupBy,
      orderBy: 'desc',
      sortBy: [{ metric: selectedTrendMetric, order: 'desc' }],
    },
  ];
}

const useGetTimelineViewerIds = (
  viewerId: string,
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = customerOverrideForDrilldown[customerId] || USER_ID_FIELD;
  return useQuery(
    [selectedTrendMetric, interval, 'query_viewer_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          interval,
          mainField: dimensionAppUserId,
          selectedTrendMetric,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        const data = get(d, 'data.0.data.0.results', []);
        const arr = data.map((e) => e[dimensionAppUserId]);
        return arr;
      });
      return res;
    },
    { enabled: !!viewerId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const useGetTimelineClientIds = (
  viewerId: string,
  selectedTrendMetric: string,
  interval: string,
) => {
  const source = axios.CancelToken.source();
  return useQuery(
    [selectedTrendMetric, interval, 'query_client_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          extraGroupBy: [],
          interval,
          mainField: CLIENT_ID_FIELD,
          selectedTrendMetric,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    { enabled: !viewerId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const useGetPersistence = (pageKey: string) => {
  const key = pageKey || getPersistentHashKey();
  return useQuery(
    [key],
    async () => {
      const res = await instance.get(`/persistence?key=${encodeURIComponent(key)}`, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      return res?.data;
    },
    { retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const usePutPersistence = (pageKey: string) => {
  const key = pageKey || getPersistentHashKey();
  return useMutation(async (config: any) => {
    const res = await instance.put(`/persistence`, {
      key,
      configuration: config,
    });
    return res?.data;
  });
};

const useGetClientIdsByViewerId = (
  viewerId: string,
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = customerOverrideForDrilldown[customerId] || USER_ID_FIELD;
  return useQuery(
    [viewerId, selectedTrendMetric, interval, 'query_client_id_by_viewer_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        [
          {
            type: 'group-by',
            dataset: 'AppExperience',
            metrics: [selectedTrendMetric],
            interval,
            granularity: 'ALL',
            filter: [
              [
                {
                  display: viewerId,
                  field: dimensionAppUserId,
                  key: null,
                  op: 'eq',
                  originalField: dimensionAppUserId,
                  value: null,
                },
              ],
            ],
            limit: 1500,
            groupBy: [CLIENT_ID_FIELD, DEVICE_NAME_FIELD],
            orderBy: 'desc',
            sortBy: [{ metric: selectedTrendMetric, order: 'desc' }],
            options: { withTotals: true },
          },
        ],
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    { enabled: !!viewerId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const useGetViewerIdsByClientId = (
  viewerId: string,
  clientId: string,
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = CLIENT_ID_FIELD;
  return useQuery(
    [clientId, selectedTrendMetric, interval, 'query_viewer_id_by_client_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        [
          {
            type: 'group-by',
            dataset: 'AppExperience',
            metrics: [selectedTrendMetric],
            interval,
            granularity: 'ALL',
            filter: [
              [
                {
                  display: clientId,
                  field: dimensionAppUserId,
                  key: null,
                  op: 'eq',
                  originalField: dimensionAppUserId,
                  value: null,
                },
              ],
            ],
            limit: 1500,
            groupBy: [customerOverrideForDrilldown[customerId] || USER_ID_FIELD, DEVICE_NAME_FIELD],
            orderBy: 'desc',
            sortBy: [{ metric: selectedTrendMetric, order: 'desc' }],
            options: { withTotals: true },
          },
        ],
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    { enabled: !viewerId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

export {
  useGetAppDataTimeline,
  useGetTimelineViewerIds,
  useGetPersistence,
  usePutPersistence,
  useGetClientIdsByViewerId,
  useGetTimelineClientIds,
  useGetViewerIdsByClientId,
};
